/**
 * ScheduleHistoryDialog — a schedule's delivery history (Story 7.9).
 *
 * A §4 centered modal (Radix DialogPrimitive + motion/react, mirroring
 * SchedulesCard's DeleteScheduleDialog scaffold) opened from the per-schedule
 * "History" button. Lists the delivery-log rows newest-first: a colored status
 * badge (failed/bounced highlighted), the triggered date, recipient count, and —
 * only when the schedule has tracking on — open/click counts. A Retry button
 * appears on failed/bounced rows for owner/admin (MANAGE_REPORTS).
 *
 * project_react_query_test_provider_gotcha: the `useQuery(["schedule-logs", id])`
 * here MUST be mocked in any test that transitively renders this dialog.
 */

import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { ArrowClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import {
  getScheduleLogs,
  retryScheduleLog,
  type DeliveryLog,
} from "../../lib/schedulesApi";
import { usePermission, Actions } from "../../hooks/usePermission";
import { statusMeta } from "./deliveryStatus";
import type { ToastType } from "../../hooks/useToast";

const CARD_BORDER = "0.5px solid var(--hairline)";

interface ScheduleHistoryDialogProps {
  scheduleId: string;
  /** Whether THIS schedule has open/click tracking on (gates the count columns). */
  trackingEnabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showToast: (type: ToastType, message: string) => void;
}

/** "Jun 15, 2026, 9:00 AM" — local formatting via Intl. */
function formatTriggered(ms: number): string {
  if (!ms) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(ms));
}

export function ScheduleHistoryDialog({
  scheduleId,
  trackingEnabled,
  open,
  onOpenChange,
  showToast,
}: ScheduleHistoryDialogProps) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Actions.MANAGE_REPORTS);
  const queryClient = useQueryClient();

  // Grow-a-page pagination: a daily schedule passes the 20-row default page in
  // under a month, so older runs must stay reachable + retryable. "Load more"
  // bumps the page size and refetches the whole (modal-scoped) list.
  const PAGE_STEP = 20;
  const [pageSize, setPageSize] = useState(PAGE_STEP);

  const {
    data: logs,
    isLoading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ["schedule-logs", scheduleId, pageSize],
    enabled: open,
    queryFn: () => getScheduleLogs(scheduleId, { limit: pageSize }),
    // Keep the current rows on screen while "Load more" fetches the larger page
    // (append-like growth, no full-list skeleton flash). Safe across schedules
    // because the dialog is keyed by scheduleId at the call site (fresh instance).
    placeholderData: keepPreviousData,
  });

  // A full page came back → there may be more to load. Stay visible (showing
  // "Loading…") while a grow-fetch is in flight, so the just-clicked button
  // doesn't flicker out (keepPreviousData holds the prior, shorter page meanwhile).
  const mayHaveMore = Boolean(
    logs && (logs.length === pageSize || isFetching),
  );

  const retryMutation = useMutation({
    mutationFn: (logId: string) => retryScheduleLog(scheduleId, logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-logs", scheduleId] });
      showToast("success", "Delivery re-queued");
    },
    onError: (err: Error) => {
      showToast("error", err.message ?? "Failed to retry delivery");
    },
  });

  const hasLogs = Boolean(logs && logs.length > 0);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 flex max-h-[80vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl border-[0.5px] border-input bg-background p-7 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="schedule-history-dialog"
              >
                <div className="flex items-center gap-2">
                  <ClockCounterClockwise size={18} className="text-foreground" />
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delivery history
                  </DialogPrimitive.Title>
                </div>
                <DialogPrimitive.Description className="mt-1.5 text-sm text-muted-foreground">
                  Each scheduled run, with its status and result.
                </DialogPrimitive.Description>

                <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
                  {isLoading ? (
                    <div className="space-y-3" data-testid="history-loading">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="h-10 animate-pulse rounded-lg bg-muted"
                        />
                      ))}
                    </div>
                  ) : isError && !hasLogs ? (
                    // Only take over the whole body when there are NO rows to show.
                    // A transient "Load more" refetch error keeps the already-loaded
                    // rows on screen (keepPreviousData) rather than discarding them.
                    <p
                      className="py-8 text-center text-sm text-muted-foreground"
                      data-testid="history-error"
                    >
                      Couldn&apos;t load delivery history. Please try again.
                    </p>
                  ) : !hasLogs ? (
                    <p
                      className="py-8 text-center text-sm text-muted-foreground"
                      data-testid="history-empty"
                    >
                      No deliveries yet — this schedule hasn&apos;t run.
                    </p>
                  ) : (
                    <ul className="divide-y-[0.5px] divide-input/70">
                      {logs!.map((log) => (
                        <HistoryRow
                          key={log.id}
                          log={log}
                          trackingEnabled={trackingEnabled}
                          canManage={canManage}
                          retrying={
                            retryMutation.isPending &&
                            retryMutation.variables === log.id
                          }
                          onRetry={() => retryMutation.mutate(log.id)}
                        />
                      ))}
                    </ul>
                  )}

                  {hasLogs && mayHaveMore && (
                    <div className="mt-3 flex justify-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="rounded-xl text-[12px] cursor-pointer text-muted-foreground hover:bg-muted"
                        disabled={isFetching}
                        onClick={() => setPageSize((p) => p + PAGE_STEP)}
                        data-testid="history-load-more"
                      >
                        {isFetching ? "Loading…" : "Load more"}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-5 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: CARD_BORDER }}
                    onClick={() => onOpenChange(false)}
                  >
                    Close
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

function HistoryRow({
  log,
  trackingEnabled,
  canManage,
  retrying,
  onRetry,
}: {
  log: DeliveryLog;
  trackingEnabled: boolean;
  canManage: boolean;
  retrying: boolean;
  onRetry: () => void;
}) {
  const meta = statusMeta(log.status);
  return (
    <li
      className={`flex items-center justify-between gap-3 py-3 ${
        meta.isError ? "-mx-2 rounded-lg bg-red-500/5 px-2" : ""
      }`}
      data-testid="history-row"
      data-status={log.status}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.badgeClass}`}
            data-testid="history-status-badge"
          >
            {meta.label}
          </span>
          <span className="text-[13px] text-muted-foreground">
            {formatTriggered(log.triggeredAt)}
          </span>
        </div>
        <p className="mt-0.5 text-[12px] text-muted-foreground/80">
          {log.recipientCount}{" "}
          {log.recipientCount === 1 ? "recipient" : "recipients"}
          {trackingEnabled && (
            <span data-testid="history-tracking-counts">
              {" "}
              · {log.openCount} {log.openCount === 1 ? "open" : "opens"} ·{" "}
              {log.clickCount} {log.clickCount === 1 ? "click" : "clicks"}
            </span>
          )}
        </p>
        {meta.isError && log.errorMessage && (
          <p
            className="mt-0.5 truncate text-[12px] text-red-600"
            data-testid="history-error-message"
          >
            {log.errorMessage}
          </p>
        )}
      </div>

      {meta.isError && canManage && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 rounded-xl text-[12px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
          style={{ border: CARD_BORDER }}
          disabled={retrying}
          onClick={onRetry}
          data-testid="history-retry-button"
        >
          <ArrowClockwise size={13} className="mr-1" />
          {retrying ? "Retrying…" : "Retry"}
        </Button>
      )}
    </li>
  );
}
